// Admin — stav napojení na preseason Google Sheets databázi.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CloudArrowDown, ArrowsClockwise, CheckCircle, XCircle, Table } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";

const COLL_LABELS = {
    gs_clubs: "Kluby", gs_matches: "Zápasy", gs_player_performances: "Hráčské výkony", gs_player_profiles: "Hráči souhrn",
};

export default function AdminPreseasonData() {
    const [diag, setDiag] = useState(null);
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        api.get("/admin/preseason-db/test", { timeout: 60000 }).then((r) => setDiag(r.data)).catch(() => setDiag({ connected: false, error: "Nemáš admin přístup nebo backend nedostupný" }));
        api.get("/admin/preseason-db/status", { timeout: 60000 }).then((r) => setStatus(r.data)).catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);

    const runSync = async () => {
        setBusy(true);
        try {
            const d = await api.post("/admin/preseason-db/sync", {}, { timeout: 120000 }).then((r) => r.data);
            toast.success(`Synchronizace hotová — ${d.inserted} nových, ${d.updated} aktualizovaných`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Synchronizace selhala"); }
        finally { setBusy(false); }
    };

    return (
        <AppShell>
            <div className="space-y-5" data-testid="admin-preseason-data">
                <header className="border-2 border-fa-sky/40 p-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-sky flex items-center gap-2">
                        <CloudArrowDown size={11} /> Admin · Preseason databáze
                    </div>
                    <h1 className="font-display font-black uppercase tracking-tight text-3xl mt-1">Google Sheets sync</h1>
                </header>

                <div className="grid lg:grid-cols-2 gap-4">
                    <section className="border border-white/10 bg-[#0d0d0d] p-4 space-y-2" data-testid="gs-connection">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/45">Připojení</div>
                        {diag && (
                            <>
                                <div className={`font-mono text-[12px] flex items-center gap-2 ${diag.connected ? "text-fa-mint" : "text-red-300"}`}>
                                    {diag.connected ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
                                    {diag.connected ? "Připojeno" : `Chyba: ${diag.error}`}
                                </div>
                                {diag.connected && (
                                    <>
                                        <div className="font-mono text-[9px] text-white/45 break-all">ID: {diag.spreadsheet_id}</div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {(diag.sheets || []).map((s) => (
                                                <span key={s} className="font-mono text-[9px] px-2 py-0.5 border border-white/15 flex items-center gap-1">
                                                    <Table size={9} /> {s} <span className="text-white/40">({diag.data_rows?.[s] ?? "?"})</span>
                                                </span>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        <button type="button" onClick={runSync} disabled={busy} data-testid="gs-sync-btn"
                                className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 border border-fa-mint text-fa-mint hover:bg-fa-mint hover:text-black font-bold flex items-center gap-1.5 disabled:opacity-40">
                            <ArrowsClockwise size={12} className={busy ? "animate-spin" : ""} /> {busy ? "Synchronizuji…" : "Synchronizovat data"}
                        </button>
                        <p className="font-mono text-[8px] text-white/35">Automaticky každých 30 minut. Souběžné běhy jsou blokované.</p>
                    </section>

                    <section className="border border-white/10 bg-[#0d0d0d] p-4 space-y-2" data-testid="gs-counts">
                        <div className="font-mono text-[9px] uppercase tracking-widest text-white/45">Interní databáze</div>
                        {status && (
                            <>
                                <div className="grid grid-cols-2 gap-2">
                                    {Object.entries(status.counts || {}).map(([k, v]) => (
                                        <div key={k} className="border border-white/10 p-2">
                                            <div className="font-display font-black text-xl tabular-nums text-fa-sky">{v}</div>
                                            <div className="font-mono text-[8px] uppercase text-white/45">{COLL_LABELS[k] || k}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="font-mono text-[9px] text-white/55">
                                    Duplicity: {Object.values(status.duplicates || {}).flat().length === 0
                                        ? <span className="text-fa-mint">žádné ✓</span>
                                        : <span className="text-red-300">{JSON.stringify(status.duplicates)}</span>}
                                </div>
                                <div className="font-mono text-[9px] text-white/55">
                                    Chybějící pole: zápasy bez data {status.missing_fields?.gs_matches_bez_datumu} · výkony bez minut {status.missing_fields?.gs_performances_bez_minut}
                                </div>
                                {status.last_success && (
                                    <div className="font-mono text-[9px] text-white/45">Poslední úspěšný sync: {String(status.last_success.finished_at).slice(0, 19).replace("T", " ")} UTC</div>
                                )}
                            </>
                        )}
                    </section>
                </div>

                <section className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="gs-runs">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-white/45 mb-2">Posledních 20 synchronizací</div>
                    <table className="w-full text-left">
                        <thead>
                            <tr className="font-mono text-[8px] uppercase tracking-widest text-white/40 border-b border-white/10">
                                <th className="px-2 py-1.5">Start</th><th className="px-2 py-1.5">Stav</th>
                                <th className="px-2 py-1.5 text-right">Zápasy</th><th className="px-2 py-1.5 text-right">Hráči</th>
                                <th className="px-2 py-1.5 text-right">Nové</th><th className="px-2 py-1.5 text-right">Změněné</th>
                                <th className="px-2 py-1.5">Spustil</th><th className="px-2 py-1.5">Chyba</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(status?.runs || []).map((r) => (
                                <tr key={r.id} className="border-b border-white/5 font-mono text-[10px]">
                                    <td className="px-2 py-1 text-white/60">{String(r.started_at).slice(5, 19).replace("T", " ")}</td>
                                    <td className={`px-2 py-1 ${r.status === "success" ? "text-fa-mint" : r.status === "failed" ? "text-red-300" : "text-fa-yellow"}`}>{r.status}</td>
                                    <td className="px-2 py-1 text-right tabular-nums text-white/60">{r.matches_read}</td>
                                    <td className="px-2 py-1 text-right tabular-nums text-white/60">{r.players_read}</td>
                                    <td className="px-2 py-1 text-right tabular-nums text-fa-mint">{r.inserted}</td>
                                    <td className="px-2 py-1 text-right tabular-nums text-white/70">{r.updated}</td>
                                    <td className="px-2 py-1 text-white/45">{r.triggered_by}</td>
                                    <td className="px-2 py-1 text-red-300/80">{r.error_message || ""}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            </div>
        </AppShell>
    );
}
